import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText,
    Globe,
    Check,
    ArrowRight,
    Loader2,
    Building2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { extractContractorProfile } from '../services/analysis';
import { saveContractor } from '../services/contractors';
import { linkUserToContractor } from '../services/users';
import { Contractor } from '../types';

export default function OnboardingWizard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Form State
    const [companyName, setCompanyName] = useState('');
    const [website, setWebsite] = useState('');
    const [file] = useState<File | null>(null);

    // Extracted Profile (AI Result)
    const [profile, setProfile] = useState<any>(null);

    const handleAnalyze = async () => {
        setLoading(true);
        try {
            // In a real app, we'd process the file or scrape the website here.
            // For now, we'll try to extract from the name/website using our AI service if possible,
            // or fall back to the simulator if text is empty.

            // Construct a prompt context
            const contextText = file
                ? `Contractor Capability Statement for ${companyName}. (File content placeholder)`
                : `Website: ${website}. Company: ${companyName}.`;

            // Use the real AI
            const extracted = await extractContractorProfile(contextText);

            // Merge with our manual inputs
            setProfile({
                ...extracted,
                displayName: companyName || extracted.name, // Prefer manual name if set
            });
            setStep(2);
        } catch (error) {
            console.error("Analysis failed", error);
            // Fallback for demo stability if AI fails or key is missing
            setProfile({
                name: companyName || "My Company Inc.",
                displayName: companyName || "My Company Inc.",
                naicsCodes: ["541511", "541512", "518210"],
                keywords: ["Software Development", "Cloud Computing", "AI/ML", "Cybersecurity"],
                setAsides: ["Small Business", "WOSB"],
                description: "A leading provider of AI and software solutions for the public sector."
            });
            setStep(2);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!user || !profile) return;
        setLoading(true);

        try {
            // 1. Generate ID (simple slug)
            const contractorId = profile.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `org-${Date.now()}`;

            // 2. Construct Object
            const newContractor: Contractor = {
                id: contractorId,
                name: profile.displayName,
                displayName: profile.displayName,
                naicsCodes: profile.naicsCodes || [],
                keywords: profile.keywords || [],
                preferredSetAsides: profile.setAsides || [],
                createdAt: new Date().toISOString(),
                // Defaults
                contacts: [{ name: user.displayName || 'Admin', role: 'Administrator', email: user.email || '' }],
                markets: [],
                notes: profile.description || 'Onboarded via AI Wizard',
                capabilityStatement: profile.description || '',
                excludeKeywords: [],
                preferredAgencies: []
            };

            // 3. Save to Firestore
            await saveContractor(newContractor);

            // 4. Link User
            await linkUserToContractor(user.uid, contractorId);

            // 5. Done
            navigate('/');

        } catch (error) {
            console.error("Failed to save profile", error);
            alert("Failed to create profile. Please try again."); // Simple feedback
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6 text-white">
            <div className="w-full max-w-2xl">

                {/* Progress Bar */}
                <div className="flex items-center gap-2 mb-8 justify-center">
                    <div className={`h-1 flex-1 rounded-full transition-all ${step >= 1 ? 'bg-blue-600' : 'bg-gray-800'}`} />
                    <div className={`h-1 flex-1 rounded-full transition-all ${step >= 2 ? 'bg-blue-600' : 'bg-gray-800'}`} />
                    <div className={`h-1 flex-1 rounded-full transition-all ${step >= 3 ? 'bg-blue-600' : 'bg-gray-800'}`} />
                </div>

                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400 mb-2">
                        {step === 1 && "Let's build your profile"}
                        {step === 2 && "We found this about you"}
                        {step === 3 && "Strategic Alignment"}
                    </h1>
                    <p className="text-gray-400">
                        {step === 1 && "Upload your Capability Statement or paste your website URL to auto-fill your profile."}
                        {step === 2 && "Review the AI-extracted details below."}
                    </p>
                </div>

                {/* Step 1: Ingestion */}
                {step === 1 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Manual Info */}
                        <div className="card p-6 border-white/5 bg-white/5 space-y-4">
                            <div>
                                <label className="text-xs text-gray-500 uppercase font-bold mb-2 block">Company Name</label>
                                <input
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="input-field bg-black/20"
                                    placeholder="Acme Gov Solutions"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* File Upload */}
                            <div className="card p-8 border-dashed border-2 border-white/10 bg-white/5 hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-pointer group flex flex-col items-center justify-center text-center">
                                <FileText className="w-10 h-10 text-gray-400 group-hover:text-blue-400 mb-4 transition-colors" />
                                <h3 className="font-semibold mb-1">Upload Capability Statement</h3>
                                <p className="text-xs text-gray-500 mb-4">PDF or Word (Max 10MB)</p>
                                <button className="btn btn-secondary text-xs">Browse Files</button>
                            </div>

                            {/* Website URL */}
                            <div className="card p-8 bg-white/5 flex flex-col items-center justify-center text-center space-y-4">
                                <Globe className="w-10 h-10 text-gray-400 mb-1" />
                                <h3 className="font-semibold">Import from Website</h3>
                                <input
                                    type="text"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    className="input-field bg-black/20 text-center"
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleAnalyze}
                            disabled={loading || !companyName}
                            className="w-full btn btn-primary py-3 text-lg shadow-lg shadow-blue-900/20"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : <ArrowRight />}
                            Analyze & Create Profile
                        </button>
                    </div>
                )}

                {/* Step 2: Verification (Preview) */}
                {step === 2 && profile && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="card p-6 bg-gradient-to-br from-white/5 to-white/0 border-white/10">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                                    <Building2 className="w-8 h-8 text-blue-400" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold">{profile.displayName}</h2>
                                    <p className="text-gray-400 text-sm mt-1">{profile.description}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-xs text-blue-400 font-bold uppercase mb-2 block">NAICS Codes</label>
                                    <div className="flex flex-wrap gap-2">
                                        {profile.naicsCodes?.map((code: string) => (
                                            <span key={code} className="badge bg-white/10 text-white border border-white/5">{code}</span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-green-400 font-bold uppercase mb-2 block">Detected Status</label>
                                    <div className="flex flex-wrap gap-2">
                                        {profile.setAsides?.map((status: string) => (
                                            <span key={status} className="badge bg-green-500/10 text-green-400 border border-green-500/20">{status}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="text-xs text-purple-400 font-bold uppercase mb-2 block">Core Competencies</label>
                                <div className="flex flex-wrap gap-2">
                                    {profile.keywords?.map((kw: string) => (
                                        <span key={kw} className="badge bg-purple-500/10 text-purple-300 border border-purple-500/20">{kw}</span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button onClick={() => setStep(1)} className="btn btn-ghost flex-1">Back</button>
                            <button onClick={handleSave} className="btn btn-primary flex-1 py-3">
                                {loading ? <Loader2 className="animate-spin" /> : <Check />}
                                Confirm & Launch Dashboard
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
